import { useApolloClient } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../app/hooks';
import { GET_SAM_CHAT_VARIABLES } from '../util/gql';
import {
  BLANK_SAM_VARIABLES,
  SamVariables,
  loadSamWidget,
  openSamWidget,
  samBottomClearance,
} from '../util/samChat';

/** The launcher's gap above whatever bar it clears. */
const GAP_PX = 12;

/**
 * Keeps the launcher above the booking, Back / Next and consent bars (it
 * used to hide behind them, so a phone never showed it on the booking
 * steps): re-measured on scroll, resize and whenever the page changes.
 */
function useBottomClearance(active: boolean): number {
  const [lift, setLift] = useState(0);
  useEffect(() => {
    if (!active || typeof window === 'undefined') return undefined;
    let frame = 0;
    const measure = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setLift(samBottomClearance());
      });
    };
    measure();
    const MO = (window as unknown as {
      MutationObserver?: typeof MutationObserver;
    }).MutationObserver;
    const observer = MO ? new MO(measure) : null;
    observer?.observe(document.body, { childList: true, subtree: true });
    observer?.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-apr-consent', 'style'],
    });
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
    };
  }, [active]);
  return lift;
}

/**
 * "Chat with Sam" (Aperio preset only; index.tsx mounts it). A tap loads
 * our chat box (util/samChat) and opens it; closing it brings the button
 * back. A signed-in customer's first name,
 * status and car go along as dynamic variables (samChatVariables, their
 * own data only, fetched on the tap); anyone else is a visitor Sam does
 * not know. Styled through `apr-sam-launch` in theme.css; it shows on
 * every page of the booking app (owner, 2026-10-27) and rides above the
 * booking's action bars and the consent bar (useBottomClearance).
 */
export default function SamChat() {
  const { t, i18n } = useTranslation();
  const client = useApolloClient();
  const signedIn = useAppSelector((state) => !!state.auth?.isAuthenticated);
  const [state, setState] = useState<'idle' | 'loading' | 'open'>('idle');
  const lift = useBottomClearance(state !== 'open');
  // The fleet pages open the fleet team, so the button says so (owner,
  // 2026-10-28: /fleet/signup said "Chat with Sam"), as on the site.
  const { pathname } = useLocation();
  const fleetPage = /^\/(?:fr\/|es\/)?(?:fleet[^/]*|dsp|partners)(?:\/|$)/.test(
    pathname || '/',
  );

  if (state === 'open') return null;
  // A fleet driver's sign-out screen is a work tool with its own bottom bar.
  if (/^\/driver(?:\/|$)/.test(pathname || '/')) return null;

  const start = async () => {
    if (state !== 'idle') return;
    setState('loading');
    let vars: SamVariables = { ...BLANK_SAM_VARIABLES };
    if (signedIn) {
      try {
        const { data } = await client.query({
          query: GET_SAM_CHAT_VARIABLES,
          fetchPolicy: 'network-only',
        });
        const v = data?.samChatVariables;
        if (v) {
          vars = {
            ...vars,
            first_name: v.firstName || '',
            customer_status: v.customerStatus || 'unknown',
            vehicle: v.vehicle || '',
          };
        }
      } catch {
        // Sam still chats; it just does not know who this is.
      }
    }
    try {
      const api = await loadSamWidget();
      setState('open');
      openSamWidget(api, vars, i18n.language, () => setState('idle'));
    } catch {
      setState('idle');
    }
  };

  return (
    <button
      type="button"
      className="apr-sam-launch"
      style={lift > 0 ? { bottom: `${lift + GAP_PX}px` } : undefined}
      aria-haspopup="dialog"
      aria-busy={state === 'loading' || undefined}
      onClick={start}
      data-testid="apr-sam-launch"
    >
      {fleetPage ? t('sam.launchFleet') : t('sam.launch')}
    </button>
  );
}
